<template>
  <Teleport to="body">
    <Transition name="info-panel-fade">
      <div v-if="show"
           class="info-panel-backdrop"
           @click.self="$emit('close')">
        <div ref="panelEl"
             class="info-panel"
             :style="panelStyle"
             role="dialog"
             aria-modal="true">

          <!-- Header -->
          <div class="info-panel-header">
            <div class="info-panel-header-left">
              <Info :size="16" class="info-panel-icon" />
              <span class="info-panel-version">Loop v{{ frontendVersion }}</span>
            </div>
            <button class="info-panel-close" @click="$emit('close')" :aria-label="$t('Webportal.Dashboard.Close')">
              <X :size="14" />
            </button>
          </div>

          <!-- Links: Imprint & Privacy -->
          <div class="info-panel-section">
            <router-link :to="{ name: 'Imprint' }" class="info-panel-item" @click="$emit('close')">
              <ScrollText :size="14" class="info-panel-item-icon" />{{ $t('Webportal.Imprint') }}
            </router-link>
            <router-link :to="{ name: 'DataProtection' }" class="info-panel-item" @click="$emit('close')">
              <FolderLock :size="14" class="info-panel-item-icon" />{{ $t('Webportal.DataProtection') }}
            </router-link>
          </div>

          <div class="info-panel-divider"></div>

          <!-- Contact & Support -->
          <div  class="info-panel-section"
                v-if="supportInformation.Phone || supportInformation.EMail">
            <div class="info-panel-section-label">{{ $t('Webportal.Dashboard.Support') }}</div>
            <a  v-if="supportInformation.Phone"
                :href="`tel:${supportInformation.Phone}`"
                class="info-panel-item">
              <Phone :size="14" class="info-panel-item-icon" />
              <span>{{ supportInformation.Phone }}</span>
            </a>
            <a  v-if="supportInformation.EMail"
                :href="`mailto:${supportInformation.EMail}`"
                class="info-panel-item">
              <Mail :size="14" class="info-panel-item-icon" />
              <span>{{ supportInformation.EMail }}</span>
            </a>
          </div>

          <!-- Footer -->
          <div class="info-panel-footer">
            <p class="info-panel-copyright">© {{ currentYear }} <b>BOLD</b> Solution GmbH</p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ScrollText, FolderLock, Mail, X, Info, Phone } from 'lucide-vue-next';
import AppConfig from "@/mixins/AppConfig-APP_TARGET";

export default {
  name: 'AppInfoPanel',

  mixins: [AppConfig],

  components: {
    ScrollText,
    FolderLock,
    Mail,
    X,
    Info,
    Phone
  },

  props: {
    show:      { type: Boolean, default: false },
    anchorEl:  { type: Object, default: null },  // ref to the trigger button element
    placement: { type: String, default: 'top-right' } // 'top-right' for sidebar, 'bottom-left' for header
  },

  emits: ['close'],

  data() {
    return {
      panelStyle: {}
    };
  },

  watch: {
    show(val) {
      if (val) {
        this.$nextTick(() => this.calcPosition());
      }
    }
  },

  computed: {
    frontendVersion() {
      return FRONTEND_VERSION;
    },
    currentYear() {
      return new Date().getFullYear();
    }
  },

  methods: {
    calcPosition() {
      const el = this.anchorEl;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const panelWidth = 260;
      const gap = 8;

      if (this.placement === 'top-right') {
        // Above the button, aligned to the right edge of the button or left side if needed
        this.panelStyle = {
          position: 'fixed',
          bottom: (window.innerHeight - rect.top + gap) + 'px',
          left: rect.right + 'px',
        };
      } else {
        // Below the button, aligned so right edge of panel = right edge of button
        this.panelStyle = {
          position: 'fixed',
          top: (rect.bottom + gap) + 'px',
          right: (window.innerWidth - rect.right) + 'px',
        };
      }
    }
  }
};
</script>

<style scoped>
.info-panel-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9999;
}

.info-panel {
  position: fixed;
  z-index: 9999;
  min-width: 260px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--bs-tertiary-bg, var(--bs-body-bg));
  backdrop-filter: blur(24px);
  border: 1px solid var(--bs-border-color);
  box-shadow: 0 16px 48px var(--custom-black-20), inset 0 0 0 1px var(--custom-black-05);
}

.info-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid var(--bs-border-color);
}

.info-panel-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-panel-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--bs-secondary-color);
}

.info-panel-version {
  font-size: 13px;
  color: var(--bs-secondary-color);
}

.info-panel-close {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.15s;
  flex-shrink: 0;
  color: var(--bs-secondary-color);
}
.info-panel-close:hover {
  background: var(--custom-black-05);
}

.info-panel-section {
  padding: 6px 6px 2px;
}

.info-panel-section-label {
  padding: 8px 14px 4px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--bs-tertiary-color, var(--bs-secondary-color));
}

.info-panel-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border-radius: 4px;
  text-decoration: none;
  color: var(--bs-body-color);
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.15s;
}
.info-panel-item:hover {
  background: var(--custom-black-05);
  color: var(--bs-body-color);
}

.info-panel-item-icon {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  color: var(--bs-secondary-color);
}

.info-panel-divider {
  margin: 0 14px;
  height: 1px;
  background: var(--bs-border-color);
}

.info-panel-footer {
  padding: 10px 16px;
  border-top: 1px solid var(--bs-border-color);
}

.info-panel-copyright {
  margin: 0;
  font-size: 10.5px;
  color: var(--bs-tertiary-color, var(--bs-secondary-color));
}

/* Transition */
.info-panel-fade-enter-active,
.info-panel-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.info-panel-fade-enter-from,
.info-panel-fade-leave-to {
  opacity: 0;
  transform: scale(0.97) translateY(4px);
}
</style>
